import { Bell, Moon, Search, Sun } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { useAuthStore } from '@/store/auth-store';
import { cn } from '@/lib/utils';
import { getCurrentTheme, setTheme, THEME_CHANGE_EVENT, type AppTheme } from '@/lib/theme';

const pageTitles: Record<string, string> = {
  '/dashboard': 'Operations Overview',
  '/executive': 'Executive Analytics',
  '/data-sources': 'Data Sources',
  '/pipelines': 'Pipeline Command',
  '/bnba-datasets': 'Dataset Kustom BNBA',
  '/monitoring': 'Monitoring Grid',
  '/settings': 'System Settings',
};

const mobileQuickLinks = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/data-sources', label: 'Sources' },
  { to: '/pipelines', label: 'Pipelines' },
];

function resolveTitle(pathname: string): string {
  if (pathname.startsWith('/pipelines/ai-generate')) return 'AI Pipeline Workbench';
  if (pathname.startsWith('/pipelines/new')) return 'Pipeline Builder';
  if (pathname.startsWith('/pipelines/')) return 'Pipeline Builder';
  return pageTitles[pathname] ?? 'Data Warehouse Platform';
}

export function TopCommandBar() {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const title = resolveTitle(location.pathname);
  const [theme, setThemeState] = useState<AppTheme>(() => getCurrentTheme());

  useEffect(() => {
    const syncTheme = () => {
      setThemeState(getCurrentTheme());
    };

    const handleStorageThemeChange = (event: StorageEvent) => {
      if (event.key === 'theme') {
        syncTheme();
      }
    };

    window.addEventListener(THEME_CHANGE_EVENT, syncTheme as EventListener);
    window.addEventListener('storage', handleStorageThemeChange);

    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, syncTheme as EventListener);
      window.removeEventListener('storage', handleStorageThemeChange);
    };
  }, []);

  const nextTheme: AppTheme = theme === 'dark' ? 'light' : 'dark';

  const handleToggleTheme = () => {
    setTheme(nextTheme);
  };

  return (
    <header
      data-testid="top-command-bar"
      className="sticky top-0 z-20 border-b bg-surface-base/85 backdrop-blur"
    >
      <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between gap-4 px-4 md:px-6">
        <div className="min-w-0">
          <p className="font-display text-base font-semibold tracking-tight md:text-lg">{title}</p>
          <p className="truncate text-xs text-muted-foreground md:text-sm">
            Control room for data ingestion, orchestration, and intelligence workflows
          </p>
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <div className="flex items-center gap-2 rounded-full border bg-surface-elevated px-3 py-1.5 text-muted-foreground">
            <Search className="h-4 w-4" />
            <span className="text-xs">Search commands</span>
          </div>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border bg-surface-elevated text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface-elevated"
            aria-label="Notifications"
          >
            <Bell className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full border bg-surface-elevated px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-surface-focus focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface-elevated"
            aria-label={`Switch to ${nextTheme} mode`}
            onClick={handleToggleTheme}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            <span>{theme === 'dark' ? 'Light' : 'Dark'} mode</span>
          </button>
          <Link
            to="/settings"
            className={cn(
              'inline-flex items-center gap-2 rounded-full border bg-surface-elevated px-3 py-1.5',
              'text-xs font-medium text-foreground transition-colors hover:bg-surface-focus',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface-elevated',
            )}
          >
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
              {(user?.username || user?.email || 'U').charAt(0).toUpperCase()}
            </span>
            <span>{user?.username || 'Operator'}</span>
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto border-t px-4 py-2 md:hidden">
        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-2 rounded-full border bg-surface-elevated px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-surface-focus focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface-elevated"
          aria-label={`Switch to ${nextTheme} mode`}
          onClick={handleToggleTheme}
        >
          {theme === 'dark' ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
          <span>{theme === 'dark' ? 'Light' : 'Dark'}</span>
        </button>
        {mobileQuickLinks.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="inline-flex shrink-0 items-center rounded-full border bg-surface-elevated px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-surface-focus focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface-elevated"
          >
            {item.label}
          </Link>
        ))}
      </div>
    </header>
  );
}
